import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Mail, MapPin, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Use empty string for production (relative URLs) or localhost for development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: 'parent',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success(response.data.message);
      setFormData({ name: '', email: '', phone: '', interest: 'parent', message: '' });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to submit inquiry. Please try again.';
      toast.error(errorMessage);
      console.error('Contact form error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-navyBlue/5">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-16">
          <div className="inline-block bg-coralRed/10 px-6 py-2 rounded-full mb-4">
            <span className="text-coralRed font-semibold">Get Involved</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-navyBlue mb-6">Join the Doctrina Community</h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Whether you're a parent, community member, or potential partner, we'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="border-none shadow-xl bg-white">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-navyBlue mb-6">Express Your Interest</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    required
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    required
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">I am a: *</label>
                  <select
                    name="interest"
                    value={formData.interest}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-tealAccent"
                    required
                  >
                    <option value="parent">Parent/Guardian</option>
                    <option value="educator">Educator</option>
                    <option value="community">Community Member</option>
                    <option value="donor">Potential Donor/Partner</option>
                    <option value="board">Authorizing Board Member</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about your interest in Doctrina Charter School..."
                    rows={4}
                    className="w-full"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full bg-coralRed hover:bg-coralRed/90 text-white py-6 text-lg transition-transform duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Inquiry <Send className="ml-2" size={20} />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-navyBlue mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-tealAccent/10 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-tealAccent" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-navyBlue mb-1">Location</h4>
                    <p className="text-gray-600">Inland Empire, CA</p>
                    <p className="text-gray-600">Serving Romoland, Homeland, Menifee, and Winchester</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-goldenOlive/10 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="text-goldenOlive" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-navyBlue mb-1">Email</h4>
                    <p className="text-gray-600">doctrinacharterschool@gmail.com</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-navyBlue to-navyBlue/90 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Enrollment Information</h3>
              <p className="text-white/90 mb-4">
                Doctrina Charter School offers free public education for grades 6-8 with nondiscriminatory open enrollment.
              </p>
              <ul className="space-y-2 text-white/90">
                <li className="flex items-start space-x-2">
                  <span className="text-tealAccent mt-1">✓</span>
                  <span>Public lottery admission process</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-tealAccent mt-1">✓</span>
                  <span>No tuition or fees</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-tealAccent mt-1">✓</span>
                  <span>Sibling and staff child preferences</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-tealAccent mt-1">✓</span>
                  <span>600 total student capacity</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-tealAccent to-sageGreen rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">For Educators</h3>
              <p className="text-white/90 mb-4">
                Join a school where your voice matters and your expertise shapes education.
              </p>
              <ul className="space-y-2 text-white/90">
                <li className="flex items-start space-x-2">
                  <span className="text-navyBlue mt-1">✓</span>
                  <span>Competitive salary and benefits</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-navyBlue mt-1">✓</span>
                  <span>Teacher-led governance through Academic Senate</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-navyBlue mt-1">✓</span>
                  <span>AM/PM flexible scheduling</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-navyBlue mt-1">✓</span>
                  <span>Professional development opportunities</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;